// Unpublished changes: edits to existing records wait here until a manager publishes them.
// The live tables, which the public sites and CMS Navigator read, only change on create, delete and publish
import { toast } from "sonner"
import { cmsDb } from "./client"
import { PENDING_ID_PREFIX } from "./historyAdapter"

// Kept in the change log, which only the Smart CMS reads: one row per record, id "pending.<bucket>.<id>"
const TABLE = "scms_change_entry"
const BACKEND: Record<PendingArea, string> = { articles: "articles", services: "service-map", site: "site-config" }
const rowId = (bucket: string, id: string) => `${PENDING_ID_PREFIX}${bucket}.${id}`

export type PendingArea = "articles" | "services" | "site"

// bucket:id -> the record as it will be once published
export type PendingMap = Record<string, any>

export const pendingKey = (bucket: string, id: string) => `${bucket}:${id}`
export const splitKey = (key: string) => {
  const i = key.indexOf(":")
  return { bucket: key.slice(0, i), id: key.slice(i + 1) }
}

// Key order doesn't matter: a record round-tripped through the database comes back reordered
const stable = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`
  if (v && typeof v === "object") {
    return `{${Object.keys(v as object).sort().map(k => `${JSON.stringify(k)}:${stable((v as any)[k])}`).join(",")}}`
  }
  return JSON.stringify(v ?? null)
}
const same = (a: unknown, b: unknown) => stable(a) === stable(b)

// What the editors, trees and assistant see: live records with their unpublished changes on top
export function overlay<S>(live: S, pending: PendingMap, buckets: readonly string[]): S {
  if (!Object.keys(pending).length) return live
  const out: any = { ...live }
  for (const bucket of buckets) {
    const keys = Object.keys(pending).filter(k => k.startsWith(`${bucket}:`))
    if (!keys.length) continue
    out[bucket] = { ...(live as any)[bucket] }
    for (const k of keys) {
      const { id } = splitKey(k)
      // A pending change to a record deleted since is dropped
      if (out[bucket][id]) out[bucket][id] = pending[k]
    }
  }
  return out as S
}

// Only the order moved: a manager's reorder goes straight live
const onlyPosition = (a: any, b: any) => {
  if (!a || !b || a.position === b.position) return false
  const { position: _a, updatedAt: _ua, ...ra } = a
  const { position: _b, updatedAt: _ub, ...rb } = b
  return same(ra, rb)
}

export interface Staged<S> {
  live: S
  pending: PendingMap
  // Pending keys written or cleared by this change
  changedPending: string[]
  liveChanged: boolean
}

/**
 * Splits one change to the working store.
 * New records, deletions and records nobody can see yet (drafts) go live. Edits to records that are
 * live become pending, except a manager's reorder. Store-level settings (languages, defaults) go live.
 */
export function stage<S>(
  live: S, pending: PendingMap, prevWorking: S, nextWorking: S, buckets: readonly string[],
  opts: { reorderLive: boolean; isLive?: (bucket: string, entity: any) => boolean },
): Staged<S> {
  const nextLive: any = { ...live }
  const nextPending: PendingMap = { ...pending }
  const changedPending: string[] = []
  let liveChanged = false

  for (const key of Object.keys(nextWorking as any)) {
    if (buckets.includes(key)) continue
    if (!same((live as any)[key], (nextWorking as any)[key])) {
      nextLive[key] = (nextWorking as any)[key]
      liveChanged = true
    }
  }

  for (const bucket of buckets) {
    const before = ((prevWorking as any)[bucket] ?? {}) as Record<string, any>
    const after = ((nextWorking as any)[bucket] ?? {}) as Record<string, any>
    const liveBucket = ((live as any)[bucket] ?? {}) as Record<string, any>
    let bucketCopy: Record<string, any> | null = null
    const writeLive = (id: string, entity: any) => {
      bucketCopy ??= { ...liveBucket }
      if (entity === undefined) delete bucketCopy[id]
      else bucketCopy[id] = entity
      liveChanged = true
    }

    for (const [id, entity] of Object.entries(after)) {
      // Stores are deep-copied on change, so compare values, not references
      if (before[id] === entity || same(before[id], entity)) continue
      const key = pendingKey(bucket, id)
      if (!liveBucket[id]) {
        writeLive(id, entity)
      } else if (opts.isLive && !opts.isLive(bucket, liveBucket[id])) {
        // Not on the public site, so there's nothing to hold back
        writeLive(id, entity)
        if (key in nextPending) { delete nextPending[key]; changedPending.push(key) }
      } else if (opts.reorderLive && !(key in nextPending) && onlyPosition(liveBucket[id], entity)) {
        writeLive(id, entity)
      } else if (same(liveBucket[id], entity)) {
        if (key in nextPending) { delete nextPending[key]; changedPending.push(key) }
      } else if (!same(nextPending[key], entity)) {
        nextPending[key] = entity
        changedPending.push(key)
      }
    }
    for (const id of Object.keys(before)) {
      if (id in after) continue
      if (liveBucket[id]) writeLive(id, undefined)
      const key = pendingKey(bucket, id)
      if (key in nextPending) { delete nextPending[key]; changedPending.push(key) }
    }
    if (bucketCopy) nextLive[bucket] = bucketCopy
  }

  return { live: liveChanged ? nextLive as S : live, pending: nextPending, changedPending, liveChanged }
}

// Applies chosen pending changes to the live store
export function publishInto<S>(live: S, pending: PendingMap, keys: string[]): S {
  const out: any = { ...live }
  for (const key of keys) {
    const { bucket, id } = splitKey(key)
    if (!pending[key] || !out[bucket]?.[id]) continue
    out[bucket] = out[bucket] === (live as any)[bucket] ? { ...out[bucket] } : out[bucket]
    out[bucket][id] = { ...pending[key], updatedAt: Date.now() }
  }
  return out as S
}

const errText = (e: any) => e?.message || String(e)

// Saves pending changes for one team and area. Writes are batched, so a bulk change is one request
export class PendingSync {
  private team = ""
  private area: PendingArea
  private queued = new Map<string, any>()
  private timer: ReturnType<typeof setTimeout> | null = null
  author: () => { name?: string; email?: string } = () => ({})
  // False until this team's unpublished changes have loaded; until then changes go live as before
  available = false

  constructor(area: PendingArea) { this.area = area }

  configure(team: string) {
    this.team = team
    this.queued.clear()
    this.available = false
  }

  async load(): Promise<PendingMap> {
    if (!this.team) return {}
    const { data, error } = await cmsDb.from(TABLE)
      .select("entity_kind, entity_id, after")
      .eq("team_id", this.team).eq("backend", BACKEND[this.area])
      .like("id", `${PENDING_ID_PREFIX}%`).eq("reverted", false)
    if (error) {
      this.available = false
      toast.error(`Couldn't load unpublished changes: ${errText(error)}`)
      return {}
    }
    this.available = true
    const out: PendingMap = {}
    for (const r of data ?? []) {
      if (r.entity_kind && r.entity_id && r.after?.entity) out[pendingKey(r.entity_kind, r.entity_id)] = r.after.entity
    }
    return out
  }

  // entity null clears the pending change
  queue(key: string, entity: any | null) {
    if (!this.available || !this.team) return
    this.queued.set(key, entity)
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => { void this.flush() }, 200)
  }

  async flush() {
    if (this.timer) { clearTimeout(this.timer); this.timer = null }
    if (!this.queued.size) return
    const batch = [...this.queued]
    this.queued.clear()
    const who = this.author()
    const now = Date.now()
    // A published or discarded change is marked done, the way the log marks reverted entries
    const rows = batch.map(([key, entity]) => {
      const { bucket, id } = splitKey(key)
      return {
        id: rowId(bucket, id), team_id: this.team, backend: BACKEND[this.area], at_ms: now,
        action: "update", actor: "user",
        author_name: who.name ?? null, author_email: who.email ?? null, author_id: null, via_model: null,
        entity_kind: bucket, entity_id: id, entity_name: "", locale: null,
        summary: "Unpublished change", before: null,
        after: entity ? { entity } : null,
        reverted: !entity,
      }
    })
    try {
      const { error } = await cmsDb.from(TABLE).upsert(rows)
      if (error) throw error
    } catch (e) {
      toast.error(`Unpublished changes weren't saved: ${errText(e)}`, { id: "pending-save" })
    }
  }
}
